/**
 * Audit Trail Service
 * Phase 2.2: Compliance-grade activity logging with privacy controls
 * Lightweight logging with hashes instead of full payloads
 */
import { createBaseRecord } from '../types/core';

// Audit action types
export const AUDIT_ACTIONS = {
  // CRUD operations
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  
  // System operations
  APPLY_REQUIREMENTS: 'applyRequirements',
  EXPORT: 'export',
  IMPORT: 'import',
  
  // Sync operations
  SYNC_PUSH: 'syncPush',
  SYNC_PULL: 'syncPull',
  SYNC_CONFLICT: 'syncConflict',
  
  // License operations
  LICENSE_VERIFY: 'license',
  LICENSE_EXPIRE: 'licenseExpire',
  
  // User operations
  USER_INVITE: 'userInvite',
  USER_ACTIVATE: 'userActivate',
  USER_DEACTIVATE: 'userDeactivate',
  
  // Login/Auth
  LOGIN: 'login',
  LOGOUT: 'logout'
};

/**
 * Log audit event to activity log collection
 * @param {Object} collections - Collections object
 * @param {Object} event - Audit event data
 * @param {string} event.action - Action type (from AUDIT_ACTIONS)
 * @param {Object} event.actor - Actor information
 * @param {string} event.orgId - Organization ID
 * @param {string} event.vesselId - Vessel ID (optional)
 * @param {string} event.collection - Collection name (optional)
 * @param {string} event.recordId - Record ID (optional)
 * @param {Object} event.before - Before state (optional, will be hashed)
 * @param {Object} event.after - After state (optional, will be hashed)
 * @param {Object} event.details - Additional details (optional)
 */
export async function logAuditEvent(collections, event) {
  try {
    const auditRecord = createBaseRecord({
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      vesselId: event.vesselId || 'system',
      ts: new Date().toISOString(),
      actor: {
        userId: event.actor?.userId || 'system',
        email: event.actor?.email || undefined,
        device: event.actor?.device || getDeviceInfo()
      },
      orgId: event.orgId || 'default_org',
      vesselId: event.vesselId || undefined,
      action: event.action,
      collection: event.collection || undefined,
      recordId: event.recordId || undefined,
      beforeHash: event.before ? await calculateHash(event.before) : undefined,
      afterHash: event.after ? await calculateHash(event.after) : undefined,
      details: sanitizeDetails(event.details || {}),
      origin: 'audit_trail'
    });

    await collections.upsert('activityLog', auditRecord);
    
    // Console log for development

  } catch (error) {

    // Don't throw - audit logging should never break app functionality
  }
}

/**
 * Log CRUD operation
 * @param {Object} collections - Collections object
 * @param {string} action - 'create', 'update', or 'delete'
 * @param {string} collection - Collection name
 * @param {Object} record - Record data
 * @param {Object} actor - Actor information
 * @param {Object} beforeState - Previous state for updates
 */
export async function logCRUDOperation(collections, action, collection, record, actor, beforeState = null) {
  await logAuditEvent(collections, {
    action,
    actor,
    orgId: actor?.orgId || 'default_org',
    vesselId: record.vesselId,
    collection,
    recordId: record.id,
    before: beforeState,
    after: record,
    details: {
      operation: `${action}_${collection}`,
      recordType: record.origin || 'manual'
    }
  });
}

/**
 * Log requirements application
 * @param {Object} collections - Collections object
 * @param {Object} result - Requirements application result
 * @param {Object} actor - Actor information
 * @param {string} vesselId - Vessel ID
 */
export async function logRequirementsApplied(collections, result, actor, vesselId) {
  await logAuditEvent(collections, {
    action: AUDIT_ACTIONS.APPLY_REQUIREMENTS,
    actor,
    orgId: actor?.orgId || 'default_org',
    vesselId,
    details: {
      creates: result.summary?.creates || 0,
      updates: result.summary?.updates || 0,
      archives: result.summary?.archives || 0,
      snapshotId: result.snapshotId,
      configHash: result.configHash,
      packsHash: result.packsHash
    }
  });
}

/**
 * Log export operation
 * @param {Object} collections - Collections object
 * @param {Object} manifest - Export manifest
 * @param {Object} actor - Actor information
 */
export async function logExport(collections, manifest, actor) {
  const totalRecords = Object.values(manifest.counts || {})
    .reduce((total, vesselCounts) => {
      return total + Object.values(vesselCounts).reduce((sum, count) => sum + count, 0);
    }, 0);

  await logAuditEvent(collections, {
    action: AUDIT_ACTIONS.EXPORT,
    actor,
    orgId: manifest.org?.id || 'default_org',
    details: {
      vessels: manifest.vessels?.map(v => v.id) || [],
      vesselCount: manifest.vessels?.length || 0,
      totalRecords,
      manifestHash: await calculateHash(manifest),
      exportedAt: manifest.exportedAt
    }
  });
}

/**
 * Log import operation
 * @param {Object} collections - Collections object
 * @param {Object} result - Import result
 * @param {Object} actor - Actor information
 */
export async function logImport(collections, result, actor) {
  await logAuditEvent(collections, {
    action: AUDIT_ACTIONS.IMPORT,
    actor,
    orgId: result.manifest?.org?.id || 'default_org',
    details: {
      sourceOrg: result.manifest?.org?.name,
      vessels: result.manifest?.vessels?.map(v => v.id) || [],
      creates: result.totals?.creates || 0,
      updates: result.totals?.updates || 0,
      skips: result.totals?.skips || 0,
      errors: result.totals?.errors || 0,
      conflicts: result.conflicts?.length || 0,
      manifestHash: await calculateHash(result.manifest),
      importedAt: new Date().toISOString()
    }
  });
}

/**
 * Log sync operations
 * @param {Object} collections - Collections object
 * @param {string} action - 'syncPush' or 'syncPull'
 * @param {Object} result - Sync result
 * @param {Object} actor - Actor information
 */
export async function logSyncOperation(collections, action, result, actor) {
  await logAuditEvent(collections, {
    action,
    actor,
    orgId: actor?.orgId || 'default_org',
    details: {
      success: result.success || false,
      processed: result.processed || result.applied || 0,
      conflicts: result.conflicts || 0,
      failed: result.failed || 0,
      syncedAt: new Date().toISOString()
    }
  });
}

/**
 * Log license operations
 * @param {Object} collections - Collections object
 * @param {string} action - License action type
 * @param {Object} licenseData - License information
 * @param {Object} actor - Actor information
 */
export async function logLicenseOperation(collections, action, licenseData, actor) {
  await logAuditEvent(collections, {
    action,
    actor,
    orgId: licenseData?.org?.id || 'default_org',
    details: {
      plan: licenseData?.plan,
      seats: licenseData?.seats,
      expiresAt: licenseData?.expiresAt,
      licenseKey: licenseData?.key ? `${licenseData.key.slice(0, 8)}...` : undefined,
      verifiedAt: licenseData?.verifiedAt
    }
  });
}

/**
 * Log user management operations
 * @param {Object} collections - Collections object
 * @param {string} action - User action type
 * @param {Object} userData - User information
 * @param {Object} actor - Actor information
 */
export async function logUserOperation(collections, action, userData, actor) {
  await logAuditEvent(collections, {
    action,
    actor,
    orgId: actor?.orgId || 'default_org',
    details: {
      targetUser: userData?.email,
      role: userData?.role,
      status: userData?.status,
      vessels: userData?.vessels?.length || 0
    }
  });
}

/**
 * Get audit trail records with filtering
 * @param {Object} collections - Collections object
 * @param {Object} filters - Filter options
 * @param {string} filters.vesselId - Filter by vessel ID
 * @param {string} filters.action - Filter by action type
 * @param {string} filters.collection - Filter by collection
 * @param {string} filters.actor - Filter by actor user ID
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} filters.limit - Limit results (default 100)
 * @returns {Promise<Array>} Filtered audit records
 */
export async function getAuditTrail(collections, filters = {}) {
  try {
    const allRecords = await collections.list('activityLog');
    
    let filtered = allRecords.filter(record => {
      // Vessel filter
      if (filters.vesselId && record.vesselId !== filters.vesselId) {
        return false;
      }
      
      // Action filter
      if (filters.action && record.action !== filters.action) {
        return false;
      }
      
      // Collection filter
      if (filters.collection && record.collection !== filters.collection) {
        return false;
      }
      
      // Actor filter
      if (filters.actor && record.actor?.userId !== filters.actor) {
        return false;
      }
      
      // Date range filter
      const recordDate = new Date(record.ts);
      if (filters.startDate && recordDate < filters.startDate) {
        return false;
      }
      if (filters.endDate && recordDate > filters.endDate) {
        return false;
      }
      
      return true;
    });
    
    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    
    // Apply limit
    const limit = filters.limit || 100;
    return filtered.slice(0, limit);
    
  } catch (error) {

    return [];
  }
}

/**
 * Get audit statistics
 * @param {Object} collections - Collections object
 * @param {Object} filters - Filter options (same as getAuditTrail)
 * @returns {Promise<Object>} Audit statistics
 */
export async function getAuditStatistics(collections, filters = {}) {
  try {
    const records = await getAuditTrail(collections, { ...filters, limit: 10000 });
    
    const stats = {
      totalEvents: records.length,
      actions: {},
      collections: {},
      actors: {},
      timeRange: {
        oldest: null,
        newest: null
      }
    };
    
    records.forEach(record => {
      // Count by action
      stats.actions[record.action] = (stats.actions[record.action] || 0) + 1;
      
      // Count by collection
      if (record.collection) {
        stats.collections[record.collection] = (stats.collections[record.collection] || 0) + 1;
      }
      
      // Count by actor
      const actor = record.actor?.userId || 'unknown';
      stats.actors[actor] = (stats.actors[actor] || 0) + 1;
      
      // Update time range
      const recordDate = new Date(record.ts);
      if (!stats.timeRange.oldest || recordDate < stats.timeRange.oldest) {
        stats.timeRange.oldest = recordDate;
      }
      if (!stats.timeRange.newest || recordDate > stats.timeRange.newest) {
        stats.timeRange.newest = recordDate;
      }
    });
    
    return stats;
    
  } catch (error) {

    return {
      totalEvents: 0,
      actions: {},
      collections: {},
      actors: {},
      timeRange: { oldest: null, newest: null }
    };
  }
}

/**
 * Calculate SHA-256 hash of object
 * @param {Object} obj - Object to hash
 * @returns {Promise<string>} Hex hash
 */
async function calculateHash(obj) {
  try {
    const jsonString = JSON.stringify(obj, Object.keys(obj).sort());
    const encoder = new TextEncoder();
    const data = encoder.encode(jsonString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {

    return 'hash_error';
  }
}

/**
 * Sanitize details object to remove PII
 * @param {Object} details - Details object
 * @returns {Object} Sanitized details
 */
function sanitizeDetails(details) {
  const sanitized = { ...details };
  
  // Remove common PII fields
  const piiFields = ['password', 'token', 'secret', 'ssn', 'phone', 'address'];
  piiFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '[REDACTED]';
    }
  });
  
  // Truncate long strings
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'string' && sanitized[key].length > 500) {
      sanitized[key] = sanitized[key].slice(0, 500) + '...';
    }
  });
  
  return sanitized;
}

/**
 * Get basic device information for audit trails
 * @returns {string} Device info string
 */
function getDeviceInfo() {
  try {
    return `${navigator.platform} / ${navigator.userAgent.split(' ')[0]}`;
  } catch (error) {
    return 'unknown_device';
  }
}

/**
 * Create audit hook for automatic CRUD logging
 * @param {Object} collections - Collections object
 * @param {Object} actor - Current actor information
 * @returns {Object} Hook functions
 */
export function createAuditHook(collections, actor) {
  return {
    onCreate: async (collection, record) => {
      await logCRUDOperation(collections, AUDIT_ACTIONS.CREATE, collection, record, actor);
    },
    
    onUpdate: async (collection, record, beforeState) => {
      await logCRUDOperation(collections, AUDIT_ACTIONS.UPDATE, collection, record, actor, beforeState);
    },
    
    onDelete: async (collection, recordId, vesselId) => {
      await logCRUDOperation(collections, AUDIT_ACTIONS.DELETE, collection, { id: recordId, vesselId }, actor);
    }
  };
}